<template>
  <div class="upload-container">
    <MainNavbar />
    <ToastContainer :toasts="toasts" @remove="removeToast" />
    <router-link to="/ipromiseafrica/resale/seller-dashboard" class="back-to-dashboard">
      <i class="fa-solid fa-arrow-left"></i>
      Back to Dashboard
    </router-link>

    <!-- PAGE TITLE -->
    <h1 class="page-title">
      <i class="fa-solid fa-plus-circle"></i>
      Add New Product
    </h1>

    <div class="upload-card" :class="{ fadeIn: true }">

      <!-- ==== PROGRESS STEPPER ==== -->
      <div class="steps-wrapper">
        <div class="progress-line">
          <div class="progress-fill" :style="{ width: step * 33 + '%' }"></div>
        </div>

            <div class="steps">
          <div
            v-for="num in 3"
            :key="num"
            class="step"
            :class="{ active: step === num, completed: step > num }"
          >
            <i :class="stepIcons[num]"></i>
                <span class="step-number">Step {{ num }}</span>
            <div class="step-label">
              {{ num === 1 ? 'Details' : num === 2 ? 'Pricing' : 'Images' }}
            </div>
          </div>
        </div>
      </div>

      <!-- ======================
            STEP 1 — DETAILS (IMPROVED)
      ======================= -->
      <transition name="slide-fade" mode="out-in">
        <div v-if="step === 1" class="step-content" key="step1">
          <div class="step-header">
            <h2 class="title"><i class="fa-solid fa-box-open"></i> Product Details</h2>
            <p class="step-description">Fill in the basic information about your product</p>
          </div>

          <!-- Two Column Layout for Better Organization -->
          <div class="form-grid">
            <!-- Left Column -->
            <div class="form-column">
              <div class="form-group">
                <label for="productTitle">
                  <i class="fa-solid fa-heading"></i>
                  Product Title *
                </label>
                <div class="input-with-counter">
                  <input 
                    id="productTitle"
                    type="text" 
                    v-model="form.title" 
                    placeholder="e.g., Nike Air Max Running Shoes"
                    maxlength="100"
                    :class="{ 'has-value': (form.title || '').length > 0 }"
                  />
                  <div class="counter">{{ (form.title || '').length }}/100</div>
                </div>
                <p class="input-hint">Be descriptive but concise</p>
              </div>

              <div class="form-group">
                <label for="productCategory">
                  <i class="fa-solid fa-tags"></i>
                  Category *
                </label>
                <div class="category-select">
                  <select 
                    id="productCategory"
                    v-model="form.category" 
                    required
                    :class="{ 'has-value': form.category }"
                  >
                    <option value="" disabled>Choose a category</option>
                    <option>Football</option>
                    <option>Basketball</option>
                    <option>Cycling</option>
                    <option>Gym & Fitness</option>
                    <option>Swimming</option>
                    <option>Outdoor & Hiking</option>
                  </select>
                  <i class="fa-solid fa-chevron-down select-arrow"></i>
                </div>
              </div>

              <div class="form-group">
                <label for="productBrand">
                  <i class="fa-solid fa-trademark"></i>
                  Brand
                </label>
                <input 
                  id="productBrand"
                  type="text" 
                  v-model="form.brand" 
                  placeholder="e.g., Nike, Adidas"
                  :class="{ 'has-value': (form.brand || '').length > 0 }"
                />
                <p class="input-hint">Optional - leave blank if unbranded</p>
              </div>
            </div>

            <!-- Right Column -->
            <div class="form-column">
              <div class="form-group">
                <label>
                  <i class="fa-solid fa-star"></i>
                  Condition *
                </label>
                <div class="condition-grid">
                  <button
                    v-for="condition in conditions"
                    :key="condition.value"
                    class="condition-btn"
                    :class="{ 
                      'selected': form.condition === condition.value,
                      'new': condition.value === 'New',
                      'like-new': condition.value === 'Like New',
                      'gently-used': condition.value === 'Gently Used',
                      'worn': condition.value === 'Worn Useable'
                    }"
                    @click="form.condition = condition.value"
                  >
                    <i :class="condition.icon"></i>
                    <span>{{ condition.label }}</span>
                    <small>{{ condition.description }}</small>
                  </button>
                </div>
              </div>

              <div class="form-group">
                <label for="productAge">
                  <i class="fa-solid fa-calendar"></i>
                  Age of Product
                </label>
                <div class="age-control">
                  <button
                    type="button"
                    class="age-btn"
                    @click="decrementAge"
                    :disabled="form.age <= 0"
                  >
                    <i class="fa-solid fa-minus"></i>
                  </button>
                  <div class="age-display">
                    <span class="age-value">{{ form.age }}</span>
                    <span class="age-unit">
                      month{{ form.age === 1 ? '' : 's' }}
                    </span>
                  </div>
                  <button
                    type="button"
                    class="age-btn"
                    @click="incrementAge"
                  >
                    <i class="fa-solid fa-plus"></i>
                  </button>
                </div>
                <p class="input-hint">Set how long you've owned this in months</p>
              </div>
            </div>
          </div>

          <!-- Full Width Fields -->
          <div class="full-width-fields">
            <div class="form-group">
              <label for="shortDescription">
                <i class="fa-solid fa-comment"></i>
                Short Description
              </label>
              <div class="input-with-counter">
                <input 
                  id="shortDescription"
                  type="text" 
                  v-model="form.shortDescription" 
                  placeholder="Brief summary of your product"
                  maxlength="200"
                  :class="{ 'has-value': (form.shortDescription || '').length > 0 }"
                />
                <div class="counter">{{ (form.shortDescription || '').length }}/200</div>
              </div>
              <p class="input-hint">Appears in search results and product cards</p>
            </div>

            <div class="form-group">
              <label for="fullDescription">
                <i class="fa-solid fa-file-lines"></i>
                Full Description *
              </label>
              <div class="textarea-wrapper">
                <textarea 
                  id="fullDescription"
                  v-model="form.fullDescription" 
                  placeholder="Describe your product in detail. Include features, specifications, measurements, etc."
                  rows="6"
                  maxlength="1000"
                  :class="{ 'has-value': form.fullDescription.length > 0 }"
                ></textarea>
                <div class="textarea-footer">
                  <div class="counter">{{ (form.fullDescription || '').length }}/1000</div>
                  <div class="formatting-hint">
                    <i class="fa-solid fa-info-circle"></i>
                    Be detailed for better sales
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="step-navigation">
            <div class="step-progress">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: getStep1Progress() + '%' }"></div>
              </div>
              <span class="progress-text">{{ getStep1Progress() }}% Complete</span>
            </div>
            <button class="next-btn animated" @click="validateStep1">
              Continue to Pricing 
              <i class="fa-solid fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </transition>

      <!-- ======================
            STEP 2 — PRICING
      ======================= -->
      <transition name="slide-fade" mode="out-in">
        <div v-if="step === 2" class="step-content" key="step2">
          <div class="step-header">
            <h2 class="title"><i class="fa-solid fa-tags"></i> Pricing</h2>
            <p class="step-description">Set your price and earnings</p>
          </div>

          <div class="pricing-form">
            <div class="form-group">
              <label for="productPrice">
                <i class="fa-solid fa-money-bill-wave"></i>
                <span class="pricing-label-text">Desired Price (KES) *</span>
              </label>
              <div class="price-input-wrapper">
                <div class="currency-prefix">KES</div>
                <input 
                  id="productPrice"
                  type="number" 
                  v-model="form.price" 
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  :class="{ 'has-value': form.price }"
                />
                <div v-if="form.price" class="price-formatted">
                  {{ formatPrice(form.price) }} in words
                </div>
              </div>
            </div>

            <div class="negotiation-toggle">
              <div class="toggle-header">
                <i class="fa-solid fa-handshake"></i>
                <div>
                  <h4 class="pricing-section-heading">Allow Price Negotiation</h4>
                  <p>Buyers can send offers for this item</p>
                </div>
              </div>
              <label class="switch">
                <input type="checkbox" v-model="form.negotiable" />
                <span class="slider"></span>
                <span class="toggle-label">{{ form.negotiable ? 'ON' : 'OFF' }}</span>
              </label>
            </div>

            <div class="earnings-breakdown">
              <h4 class="pricing-section-heading">
                <i class="fa-solid fa-calculator"></i>
                Earnings Breakdown
              </h4>
              <div class="breakdown-grid">
                <div class="breakdown-item">
                  <span class="breakdown-label">Product Price</span>
                  <span class="amount breakdown-amount">KES {{ formatPrice(form.price || 0) }}</span>
                </div>
                <div class="breakdown-item total">
                  <span class="breakdown-label">Your Estimated Payout</span>
                  <span class="amount highlight breakdown-amount">KES {{ formatPrice(form.price || 0) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="nav-buttons">
            <button class="nav-btn secondary" @click="goBack">
              <i class="fa-solid fa-arrow-left"></i>
              Back to Details
            </button>

            <button class="nav-btn primary animated" @click="validateStep2">
              Continue to Images 
              <i class="fa-solid fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </transition>

      <!-- ======================
            STEP 3 — IMAGES
      ======================= -->
      <transition name="slide-fade" mode="out-in">
        <div v-if="step === 3" class="step-content" key="step3">
          <div class="step-header">
            <h2 class="title"><i class="fa-solid fa-image"></i> Upload Images</h2>
            <p class="step-description">Add photos to showcase your product</p>
          </div>

          <div class="upload-instructions">
            <div class="instruction-card">
              <i class="fa-solid fa-check-circle"></i>
              <div>
                <h4>Requirements</h4>
                <p>Upload 3–8 clear photos • First image becomes cover</p>
              </div>
            </div>
            <div class="instruction-card">
              <i class="fa-solid fa-image"></i>
              <div>
                <h4>Format</h4>
                <p>JPG, PNG, WebP • Max 5MB per image</p>
              </div>
            </div>
          </div>

          <div class="upload-area" @click="triggerFileInput" @dragover.prevent @drop.prevent="handleDrop">
            <div class="upload-content">
              <i class="fa-solid fa-cloud-arrow-up upload-icon"></i>
              <h3>Drop images here or click to upload</h3>
              <p class="upload-subtitle">Drag & drop your product images</p>
              <div class="upload-actions">
                <button class="upload-btn">
                  <i class="fa-solid fa-folder-open"></i>
                  Browse Files
                </button>
                <span class="or-text">or</span>
                <span class="drag-text">drag images here</span>
              </div>
            </div>
            <input type="file" ref="fileInput" multiple accept="image/*" @change="uploadImages" hidden />
          </div>

          <div v-if="form.images.length > 0" class="image-gallery">
            <div class="gallery-header">
              <div class="gallery-stats">
                <i class="fa-solid fa-images"></i>
                <span>{{ form.images.length }} image{{ form.images.length !== 1 ? 's' : '' }} selected</span>
                <span class="cover-info">
                  <i class="fa-solid fa-star"></i>
                  Image {{ form.coverIndex + 1 }} is cover
                </span>
              </div>
              <button class="clear-btn" @click="clearAllImages">
                <i class="fa-solid fa-trash"></i>
                Clear All
              </button>
            </div>

            <div class="gallery-grid">
              <div
                v-for="(img, i) in form.images"
                :key="i"
                class="gallery-item"
                :class="{ 'is-cover': i === form.coverIndex }"
                @click="setCover(i)"
              >
                <div class="gallery-item-inner">
                  <img
                    :src="img.url"
                    alt=""
                    loading="lazy"
                    decoding="async"
                    @error="removeImage(i)"
                  />
                  <div class="gallery-overlay">
                    <button class="remove-btn" @click.stop="removeImage(i)">
                      <i class="fa-solid fa-trash"></i>
                    </button>
                    <button class="view-btn" @click.stop="previewImage(img.url)">
                      <i class="fa-solid fa-expand"></i>
                    </button>
                  </div>
                  <div v-if="i === form.coverIndex" class="cover-indicator">
                    <i class="fa-solid fa-crown"></i>
                    Cover Photo
                  </div>
                  <button class="set-cover-btn" @click.stop="setCover(i)">
                    <i class="fa-solid fa-star"></i>
                    Set as Cover
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="nav-buttons">
            <button class="nav-btn secondary" @click="goBack">
              <i class="fa-solid fa-arrow-left"></i>
              Back to Pricing
            </button>

            <button
              class="nav-btn primary animated"
              @click="confirmSubmission"
              :disabled="submitting || form.images.length < 3"
            >
              <i class="fa-solid fa-check-circle"></i> 
              {{ submitting ? 'Submitting...' : (form.images.length < 3 ? 'Upload 3+ Images' : 'Review & Submit') }}
            </button>
          </div>
        </div>
      </transition>

    </div>

    <!-- ================= MODALS & NOTIFICATIONS ================= -->
    
    <!-- Compact Confirmation Modal -->
    <div v-if="showConfirmationModal" class="modal-overlay">
      <div class="modal-content compact" @click.stop>
        <div class="modal-header">
          <div class="modal-icon">
            <i class="fa-solid fa-rocket"></i>
          </div>
          <h3>Ready to List?</h3>
          <button @click="showConfirmationModal = false" class="modal-close">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="quick-summary">
            <div class="summary-item">
              <i class="fa-solid fa-box-open"></i>
              <div>
                <span class="summary-label">Product</span>
                <strong class="summary-value">{{ form.title || '—' }}</strong>
              </div>
            </div>
            <div class="summary-item">
              <i class="fa-solid fa-tag"></i>
              <div>
                <span class="summary-label">Price</span>
                <strong class="summary-value">KES {{ formatPrice(form.price ?? 0) }}</strong>
              </div>
            </div>
            <div class="summary-item">
              <i class="fa-solid fa-images"></i>
              <div>
                <span class="summary-label">Photos</span>
                <strong class="summary-value">{{ form.images.length }} uploaded</strong>
              </div>
            </div>
          </div>
          <div class="modal-consent">
            <i class="fa-solid fa-shield-halved"></i>
            <p>By submitting this listing, you consent to I Promise Africa (IPA) displaying your contact phone number to potential buyers on the marketplace to facilitate transactions.</p>
          </div>
          <div class="modal-note">
            <i class="fa-solid fa-bolt"></i>
            Your listing will go live immediately after submission
          </div>
        </div>
        <div class="modal-footer compact">
          <button class="btn-secondary" @click="showConfirmationModal = false" :disabled="submitting">
            Cancel
          </button>
          <button class="btn-primary" @click="submitProduct" :disabled="submitting">
            {{ submitting ? 'Submitting...' : 'Confirm & List' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Success Notification -->
    <div v-if="showSuccessNotification" class="notification-popup success">
      <div class="notification-content">
        <div class="notification-icon">
          <i class="fa-solid fa-check-circle"></i>
        </div>
        <button @click="showSuccessNotification = false" class="close-notification">
          <i class="fa-solid fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Image Preview Modal -->
    <div v-if="previewImageUrl" class="preview-overlay" @click="previewImageUrl = null">
      <div class="preview-content" @click.stop>
        <button class="preview-close" @click="previewImageUrl = null">
          <i class="fa-solid fa-times"></i>
        </button>
        <img
          :src="previewImageUrl"
          alt="Preview"
          decoding="async"
          @error="previewImageUrl = null"
        />
      </div>
    </div>

    <div class="upload-footer-wrap">
      <MainFooter />
    </div>
  </div>
</template>

<script>
/**
 * SellerProductUpload.vue
 *
 * Seller listing creation: 3-step form (Details → Pricing → Images) that posts
 * to POST /seller/listings/create (multipart/form-data). Requires auth; shows
 * consent and toasts for success/error.
 *
 * Steps:
 *   1. Details: title, category, brand, condition, age (months), short/full description
 *   2. Pricing: price (KES), negotiable toggle
 *   3. Images: 3–8 images, cover selection, then Review & Submit
 *
 * Flow: validate per step → confirm modal (with IPA phone consent) → submit →
 * toast + success panel (with dashboard link) or toast on error.
 */
import MainNavbar from "../../components/main/headernfooter/header.vue";
import MainFooter from "./Footer.vue";
import ToastContainer from "../../components/ui/ToastContainer.vue";
import toastMixin from "@/mixins/toastMixin";
import { buildApiUrl } from "@/config/api";

export default {
  name: "SellerProductUpload",
  mixins: [toastMixin],
  components: { MainNavbar, MainFooter, ToastContainer },

  data() {
    return {
      step: 1,
      submitting: false,
      showConfirmationModal: false,
      showSuccessNotification: false,
      previewImageUrl: null,
      toastTimers: [], /* interval IDs for cleanup on unmount */

      conditions: [
        { value: 'New', label: 'New', icon: 'fa-solid fa-gift', description: 'Never used, with tags' },
        { value: 'Like New', label: 'Like New', icon: 'fa-solid fa-sparkles', description: 'Used once or twice' },
        { value: 'Gently Used', label: 'Gently Used', icon: 'fa-solid fa-thumbs-up', description: 'Minor signs of use' },
        { value: 'Worn Useable', label: 'Worn Useable', icon: 'fa-solid fa-shoe-prints', description: 'Used but functional' }
      ],

      stepIcons: {
        1: "fa-solid fa-box",
        2: "fa-solid fa-money-check-dollar",
        3: "fa-solid fa-image",
      },

      form: {
        title: "",
        category: "",
        brand: "",
        condition: "",
        shortDescription: "",
        fullDescription: "",
        age: 0,
        price: "",
        negotiable: false,
        images: [],
        coverIndex: 0,
      },
    };
  },

  methods: {

    handleAuthError(res) {
      if (res && (res.status === 401 || res.status === 403)) {
        localStorage.removeItem("access_token");
        this.showToast("Your session has expired. Please sign in again.", "error");
        this.$router.push("/ipromiseafrica/sign-in");
        return true;
      }
      return false;
    },

    incrementAge() {
      this.form.age += 1;
    },

    decrementAge() {
      if (this.form.age > 0) {
        this.form.age -= 1;
      }
    },

    goNext() {
      if (this.step < 3) this.step++;
    },
    goBack() {
      if (this.step > 1) this.step--;
    },

    getStep1Progress() {
      const form = this.form || {};
      let progress = 0;
      if ((form.title || "").trim()) progress += 20;
      if (form.category) progress += 20;
      if (form.condition) progress += 20;
      if ((form.fullDescription || "").trim()) progress += 30;
      if ((form.shortDescription || "").trim()) progress += 10;
      return Math.min(progress, 100);
    },

    validateStep1() {
      const form = this.form || {};
      if (!(form.title || "").trim()) {
        this.showToast("Product title is required", "error");
        return;
      }
      if (!form.category) {
        this.showToast("Please select a category", "error");
        return;
      }
      if (!form.condition) {
        this.showToast("Please select product condition", "error");
        return;
      }
      if (!(form.fullDescription || "").trim()) {
        this.showToast("Full description is required", "error");
        return;
      }
      this.goNext();
    },

    validateStep2() {
      const price = this.form?.price;
      if (price == null || price === "" || Number(price) <= 0) {
        this.showToast("Please enter a valid price", "error");
        return;
      }
      this.goNext();
    },

    triggerFileInput() {
      this.$refs?.fileInput?.click();
    },

    handleDrop(event) {
      event?.preventDefault?.();
      const files = event?.dataTransfer?.files;
      this.processImageFiles(files ? Array.from(files) : []);
    },

    uploadImages(event) {
      const files = event?.target?.files;
      this.processImageFiles(files ? Array.from(files) : []);
    },

    processImageFiles(files) {
      if (!Array.isArray(files) || files.length === 0) {
        this.showToast("Please select valid image files", "error");
        return;
      }
      const invalidReasons = [];
      const validFiles = files.filter((file) => {
        if (!file?.type?.startsWith("image/")) {
          invalidReasons.push(`${file?.name || "File"} is not an image`);
          return false;
        }
        if (file.size > 5 * 1024 * 1024) {
          invalidReasons.push(`${file?.name || "File"} exceeds 5MB`);
          return false;
        }
        return true;
      });
      if (invalidReasons.length > 0) {
        this.showToast(invalidReasons.slice(0, 2).join("; ") + (invalidReasons.length > 2 ? "…" : ""), "error");
      }
      const images = this.form?.images ?? [];
      if (images.length + validFiles.length > 8) {
        this.showToast("Maximum 8 images allowed", "error");
        return;
      }
      if (validFiles.length === 0) return;
      validFiles.forEach((file) => {
        try {
          this.form.images.push({ file, url: URL.createObjectURL(file) });
        } catch (e) {
          this.showToast("Could not add image. Please try again.", "error");
        }
      });
    },

    removeImage(index) {
      const images = this.form?.images ?? [];
      const item = images[index];
      if (item?.url) URL.revokeObjectURL(item.url);
      this.form.images.splice(index, 1);
      if (this.form.coverIndex >= index && this.form.coverIndex > 0) {
        this.form.coverIndex--;
      }
      this.form.coverIndex = Math.min(this.form.coverIndex, Math.max(0, this.form.images.length - 1));
    },

    clearAllImages() {
      const images = this.form?.images ?? [];
      images.forEach((img) => {
        if (img?.url) URL.revokeObjectURL(img.url);
      });
      this.form.images = [];
      this.form.coverIndex = 0;
    },

    setCover(i) {
      this.form.coverIndex = i;
    },

    previewImage(url) {
      this.previewImageUrl = url;
    },

    formatPrice(price) {
      const n = Number(price);
      if (n !== n || n < 0) return "0";
      return n.toLocaleString("en-KE", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },

    confirmSubmission() {
      if (this.form.images.length < 3) {
        this.showToast("Please upload at least 3 images", "error");
        return;
      }
      this.showConfirmationModal = true;
    },

    async submitProduct() {
      this.showConfirmationModal = false;
      this.submitting = true;
      const form = this.form || {};

      try {
        const token = typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null;
        if (!token) {
          throw new Error("Authentication required. Please log in again.");
        }

        const formData = new FormData();
        formData.append("title", (form.title ?? "").toString());
        formData.append("category", (form.category ?? "").toString());
        formData.append("brand", (form.brand ?? "").toString());
        formData.append("condition", (form.condition ?? "").toString());
        formData.append("shortDescription", (form.shortDescription ?? "").toString());
        formData.append("fullDescription", (form.fullDescription ?? "").toString());
        const age = Number(form.age);
        formData.append("age", age > 0 ? `${age} months` : "");
        formData.append("price", String(form.price ?? ""));
        formData.append("negotiable", form.negotiable ? "true" : "false");
        formData.append("coverIndex", String(form.coverIndex ?? 0));

        const images = form.images ?? [];
        images.forEach((obj) => {
          if (obj?.file) formData.append("images", obj.file);
        });

        const res = await fetch(buildApiUrl("seller/listings/create"), {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        if (this.handleAuthError(res)) return;

        let data = {};
        try {
          const text = await res.text();
          if (text) data = JSON.parse(text);
        } catch (_) {
          /* non-JSON response */
        }

        if (!res.ok) {
          const msg = Array.isArray(data.detail)
            ? data.detail.map((e) => e?.msg ?? (e?.loc && e.loc.join(".")) ?? JSON.stringify(e)).join("; ") || res.statusText
            : (data.detail != null && String(data.detail)) || `Failed to create listing (${res.status})`;
          throw new Error(msg);
        }

        this.showToast("Product listed successfully! Your listing is now live.", "success");
        this.showSuccessNotification = true;
        setTimeout(() => {
          this.resetForm();
        }, 3000);
      } catch (error) {
        const message = error?.message && String(error.message);
        this.showToast(message || "Something went wrong. Please try again.", "error");
      } finally {
        this.submitting = false;
      }
    },

    resetForm() {
      this.form = {
        title: "",
        category: "",
        brand: "",
        condition: "",
        shortDescription: "",
        fullDescription: "",
        age: 0,
        price: "",
        negotiable: false,
        images: [],
        coverIndex: 0,
      };
      this.step = 1;
    },
  },

  beforeUnmount() {
    this.toastTimers?.forEach((id) => clearInterval(id));
    this.toastTimers = [];
    const images = this.form?.images ?? [];
    images.forEach((img) => {
      if (img?.url) URL.revokeObjectURL(img.url);
    });
  },
};
</script>

<style scoped>
/* === GLOBAL CONTAINER === */
.back-to-dashboard {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color1);
  text-decoration: none;
  font-size: 0.95rem;
  margin: 1rem;
  padding: 0.5rem 0;
}
.back-to-dashboard:hover {
  color: var(--border-color-hover);
}

.upload-container {
  display: flex;
  flex-direction: column;
  background: var(--background-color1);
  padding-top: 80px;
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  animation: fadeIn 0.4s ease-in-out;
  min-height: 100vh;
  box-sizing: border-box;
}

.upload-footer-wrap {
  margin-top: auto;
}

/* PAGE TITLE */
.page-title {
  text-align: center;
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  color: var(--text-color1);
}

.page-title i {
  color: var(--border-color-hover);
  font-size: 30px;
}

/* === CARD HOLDER === */
.upload-card {
  max-width: 1000px;
  margin: auto;
  background: var(--background-color2);
  padding: 40px;
  border-radius: 26px;
  box-shadow: 0 10px 35px rgba(0, 0, 0, 0.22);
  transition: 0.35s ease;
  border: 1px solid rgba(255,255,255,0.04);
}

.upload-card:hover {
  transform: translateY(-3px);
}

/* === IMPROVED STEPPER === */
.step-number {
  color: var(--border-color-hover);
  font-weight: 600;
}
.steps-wrapper {
  margin-bottom: 40px;
}

.progress-line {
  width: 100%;
  height: 6px;
  background: rgba(255,255,255,0.08);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 24px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--border-color-hover), #00b4ff);
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 10px;
}

.steps {
  display: flex;
  justify-content: space-between;
  position: relative;
}

.step {
  text-align: center;
  opacity: 0.6;
  transition: all 0.3s ease;
  font-size: 14px;
  flex: 1;
  position: relative;
  z-index: 1;
}

.step.active {
  opacity: 1;
  transform: scale(1.05);
}

.step.completed {
  opacity: 0.9;
}

.step i {
  display: block;
  font-size: 22px;
  margin-bottom: 8px;
  color: var(--border-color-hover);
  transition: all 0.3s ease;
}

.step.active i {
  color: #00b4ff;
  transform: scale(1.2);
}

.step-label {
  font-size: 12px;
  margin-top: 4px;
  color: #94a3b8;
}

/* === STEP HEADER === */
.step-header {
  margin-bottom: 32px;
  text-align: center;
}

.title {
  font-weight: 700;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 8px;
  color: var(--text-color1);
}

.step-description {
  color: var(--border-color-hover);
  font-size: 15px;
  margin: 0;
}

/* === IMPROVED STEP 1 FORM === */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-bottom: 32px;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }
}

.form-column {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 15px;
  color: var(--text-color1);
}

.form-group label i {
  color: var(--border-color-hover);
  width: 20px;
}

.input-with-counter {
  position: relative;
}

.input-with-counter input,
.form-group input,
.form-group textarea {
  width: 100%;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-size: 15px;
  color: var(--text-color1);
  transition: all 0.3s ease;
  font-family: inherit;
}

.input-with-counter input:focus,
.form-group input:focus,
.form-group textarea:focus,
.input-with-counter input.has-value,
.form-group input.has-value,
.form-group textarea.has-value {
  border-color: var(--border-color-hover);
  background: rgba(0, 185, 255, 0.05);
  box-shadow: 0 0 0 3px rgba(0, 185, 255, 0.1);
  outline: none;
}

.counter {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: #94a3b8;
  background: rgba(255, 255, 255, 0.05);
  padding: 2px 8px;
  border-radius: 10px;
}

.input-hint {
  font-size: 13px;
  color: #94a3b8;
  margin: 4px 0 0 0;
}

/* Category Select - theme consistent for dark/light mode */
.category-select {
  position: relative;
}

.category-select select {
  width: 100%;
  padding: 14px 16px;
  background: var(--background-color2);
  border: 2px solid var(--border-color-hover);
  border-radius: 12px;
  font-size: 15px;
  color: var(--text-color1);
  appearance: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.category-select select.has-value {
  border-color: var(--border-color-hover);
  background: var(--background-color2);
}

.category-select select option {
  background: var(--background-color2);
  color: var(--text-color1);
}

.select-arrow {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color1);
  pointer-events: none;
}

/* Condition Grid */
.condition-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

@media (max-width: 480px) {
  .condition-grid {
    grid-template-columns: 1fr;
  }
}

.condition-btn {
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.condition-btn:hover {
  transform: translateY(-2px);
  border-color: var(--border-color-hover);
  background: rgba(0, 185, 255, 0.05);
}

.condition-btn.selected {
  border-color: var(--border-color-hover);
  background: rgba(0, 185, 255, 0.1);
  box-shadow: 0 4px 12px rgba(0, 185, 255, 0.2);
}

.condition-btn i {
  font-size: 20px;
  margin-bottom: 4px;
  color: var(--text-color1);
}

.condition-btn.new.selected i { color: var(--text-color1); }
.condition-btn.like-new.selected i { color: var(--text-color1); }
.condition-btn.gently-used.selected i { color: var(--text-color1); }
.condition-btn.worn.selected i { color: var(--text-color1); }

.condition-btn span {
  font-weight: 600;
  font-size: 14px;
  color: var(--text-color1);
}

.condition-btn small {
  font-size: 12px;
  color: #94a3b8;
}

/* Age control */
.age-control {
  display: flex;
  align-items: center;
  gap: 12px;
}

.age-btn {
  min-width: 44px;
  min-height: 44px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid var(--border-color-hover);
  background: transparent;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  touch-action: manipulation;
}

.age-btn:hover:not(:disabled) {
  background: var(--border-color-hover);
  color: var(--text-color3);
  box-shadow: 0 4px 10px rgba(0, 185, 255, 0.3);
}

.age-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.age-display {
  display: flex;
  align-items: baseline;
  gap: 6px;
  color: var(--text-color1);
}

.age-value {
  font-size: 18px;
  font-weight: 700;
}

.age-unit {
  font-size: 14px;
}

/* Textarea */
.textarea-wrapper {
  position: relative;
}

.textarea-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
}

.formatting-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #94a3b8;
}

/* Full Width Fields */
.full-width-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
}

/* Step Navigation */
.step-navigation {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.step-progress {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 16px;
}

.progress-bar {
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-text {
  display: block;
  text-align: center;
  font-size: 14px;
  color: #94a3b8;
}

/* === IMPROVED STEP 2 === */
.pricing-label-text,
.pricing-section-heading,
.breakdown-label,
.breakdown-amount {
  color: var(--border-color-hover);
}

.pricing-form {
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-bottom: 32px;
}

.price-input-wrapper {
  position: relative;
}

.currency-prefix {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 600;
  color: var(--border-color-hover);
}

.price-input-wrapper input {
  padding-left: 60px;
  font-size: 18px;
  font-weight: 600;
}

.price-formatted {
  margin-top: 4px;
  font-size: 13px;
  color: #94a3b8;
  font-style: italic;
}

/* Negotiation Toggle */
.negotiation-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 20px;
  border: 2px solid rgba(255, 255, 255, 0.1);
}

.toggle-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-header i {
  font-size: 24px;
  color: var(--border-color-hover);
}

.toggle-header h4 {
  margin: 0;
  font-size: 16px;
}

.toggle-header p {
  margin: 4px 0 0 0;
  color: #94a3b8;
  font-size: 14px;
}

.negotiation-toggle .switch {
  position: relative;
  display: inline-block;
  width: 68px;
  height: 34px;
}

.negotiation-toggle .slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #374151;
  transition: .4s;
  border-radius: 34px;
}

.negotiation-toggle .slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

.negotiation-toggle input:checked + .slider {
  background-color: var(--border-color-hover);
}

.negotiation-toggle input:checked + .slider:before {
  transform: translateX(34px);
}

.toggle-label {
  position: absolute;
  right: -50px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 600;
  font-size: 14px;
  color: #94a3b8;
}

/* Earnings Breakdown */
.earnings-breakdown {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 24px;
  border: 2px solid rgba(255, 255, 255, 0.1);
}

.earnings-breakdown h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 20px 0;
  font-size: 16px;
  color: var(--text-color1);
}

.breakdown-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.breakdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.breakdown-item:last-child {
  border-bottom: none;
}

.breakdown-item.total {
  padding-top: 16px;
  border-top: 2px solid rgba(255, 255, 255, 0.2);
  font-size: 17px;
  font-weight: 700;
}

.breakdown-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 12px;
  background: rgba(0, 185, 255, 0.1);
  border-radius: 8px;
  color: var(--border-color-hover);
  font-size: 13px;
}

/* === IMPROVED STEP 3 === */
.upload-instructions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
}

.instruction-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  border: 2px solid rgba(255, 255, 255, 0.1);
}

.instruction-card i {
  font-size: 24px;
  color: var(--border-color-hover);
}

.instruction-card h4 {
  margin: 0 0 4px 0;
  font-size: 15px;
}

.instruction-card p {
  margin: 0;
  color: #94a3b8;
  font-size: 14px;
}

/* Upload Area */
.upload-area {
  border: 3px dashed var(--border-color-hover);
  border-radius: 20px;
  padding: 60px 20px;
  text-align: center;
  cursor: pointer;
  background: rgba(0, 185, 255, 0.05);
  margin-bottom: 32px;
  transition: all 0.3s ease;
}

.upload-area:hover {
  background: rgba(0, 185, 255, 0.1);
  border-color: #00b4ff;
  transform: scale(1.01);
}

.upload-content {
  max-width: 400px;
  margin: 0 auto;
}

.upload-icon {
  font-size: 64px;
  color: var(--border-color-hover);
  margin-bottom: 20px;
  opacity: 0.8;
}

.upload-content h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  color: var(--text-color1);
}

.upload-subtitle {
  color: #94a3b8;
  margin-bottom: 24px;
}

.upload-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.upload-btn {
  padding: 12px 24px;
  background: var(--border-color-hover);
  color: var(--text-color3);
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.upload-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 185, 255, 0.3);
}

.or-text {
  color: #94a3b8;
  font-weight: 600;
}

.drag-text {
  color: var(--border-color-hover);
  font-weight: 600;
}

/* Image Gallery */
.image-gallery {
  margin-bottom: 32px;
}

.gallery-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.1);
}

.clear-btn {
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  color: var(--text-color1);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.clear-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.5);
  color: #fca5a5;
}

.gallery-stats {
  display: flex;
  align-items: center;
  gap: 16px;
}

.gallery-stats i {
  color: var(--border-color-hover);
}

.cover-info {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(0, 185, 255, 0.1);
  border-radius: 8px;
  color: var(--border-color-hover);
  font-size: 14px;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
}

@media (max-width: 768px) {
  .gallery-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
}

.gallery-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.gallery-item.is-cover {
  border: 3px solid var(--border-color-hover);
  box-shadow: 0 8px 24px rgba(0, 185, 255, 0.2);
}

.gallery-item:hover {
  transform: translateY(-4px);
}

.gallery-item-inner {
  position: relative;
  width: 100%;
  height: 100%;
}

.gallery-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gallery-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.gallery-item:hover .gallery-overlay {
  opacity: 1;
}

.remove-btn,
.view-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.remove-btn {
  background: rgba(239, 68, 68, 0.9);
  color: white;
}

.remove-btn:hover {
  background: #dc2626;
  transform: scale(1.1);
}

.view-btn {
  background: rgba(255, 255, 255, 0.9);
  color: #1f2937;
}

.view-btn:hover {
  background: white;
  transform: scale(1.1);
}

.cover-indicator {
  position: absolute;
  top: 8px;
  left: 8px;
  background: var(--border-color-hover);
  color: var(--text-color3);
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
}

.set-cover-btn {
  position: absolute;
  bottom: 8px;
  left: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  border: none;
  padding: 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.gallery-item:hover .set-cover-btn {
  opacity: 1;
  transform: translateY(0);
}

.set-cover-btn:hover {
  background: var(--border-color-hover);
  color: var(--text-color3);
}

/* === COMPACT MODAL === */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  padding-left: calc(20px + env(safe-area-inset-left, 0px));
  padding-right: calc(20px + env(safe-area-inset-right, 0px));
  padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  animation: fadeIn 0.3s ease;
}

.modal-content.compact {
  background: var(--background-color2);
  border-radius: 20px;
  width: 100%;
  max-width: 400px;
  max-height: calc(100vh - 40px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.4s ease;
  border: 1px solid rgba(255,255,255,0.1);
}

.modal-header {
  padding: 24px 24px 16px;
  text-align: center;
  position: relative;
}

.modal-icon {
  width: 60px;
  height: 60px;
  background: rgba(0, 185, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.modal-icon i {
  font-size: 28px;
  color: var(--border-color-hover);
}

.modal-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: var(--text-color1);
  font-weight: 700;
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  min-width: 44px;
  min-height: 44px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  touch-action: manipulation;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.2);
  color: var(--text-color1);
}

.modal-body {
  padding: 16px 24px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.quick-summary {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
}

.summary-item i {
  font-size: 20px;
  color: var(--border-color-hover);
  width: 24px;
}

.summary-label {
  display: block;
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 2px;
}

.summary-value {
  font-size: 14px;
  color: var(--text-color1);
  font-weight: 600;
}

.modal-note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: rgba(0, 185, 255, 0.1);
  border-radius: 8px;
  color: var(--border-color-hover);
  font-size: 13px;
}

.modal-consent {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  border-left: 4px solid var(--border-color-hover);
}

.modal-consent i {
  font-size: 18px;
  color: var(--border-color-hover);
  flex-shrink: 0;
  margin-top: 2px;
}

.modal-consent p {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-color1);
}

.modal-footer.compact {
  padding: 20px 24px;
  border-top: 1px solid rgba(255,255,255,0.1);
  display: flex;
  gap: 12px;
}

.modal-footer.compact .btn-primary,
.modal-footer.compact .btn-secondary {
  flex: 1;
  padding: 12px 20px;
  border-radius: 10px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
}

.modal-footer.compact .btn-primary {
  background: var(--border-color-hover);
  color: var(--text-color3);
}

.modal-footer.compact .btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,185,255,0.3);
}

.modal-footer.compact .btn-secondary {
  background: transparent;
  border: 2px solid rgba(255,255,255,0.2);
  color: var(--text-color1);
}

.modal-footer.compact .btn-secondary:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255,255,255,0.3);
}

/* === BUTTONS === */
.next-btn,
.back-btn,
.submit-btn,
.nav-btn {
  padding: 16px 32px;
  min-height: 48px;
  border-radius: 999px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.25s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  touch-action: manipulation;
}

.next-btn,
.submit-btn,
.nav-btn.primary {
  background: var(--border-color-hover);
  color: var(--text-color3);
}

.nav-btn.secondary {
  background: transparent;
  color: var(--text-color1);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.next-btn:hover:not(:disabled),
.submit-btn:hover:not(:disabled),
.nav-btn.primary:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0,185,255,0.3);
}

.nav-btn.secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
}

.next-btn:disabled,
.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.back-btn {
  background: transparent;
  border: 2px solid var(--border-color-hover);
  color: var(--text-color3);
  min-width: 160px;
}

.back-btn:hover {
  background: var(--border-color-hover);
  color: var(--text-color3);
}

.nav-buttons {
  display: flex;
  gap: 16px;
  margin-top: 32px;
}

.nav-buttons .back-btn {
  flex: 0 0 auto;
}

.nav-buttons .next-btn,
.nav-buttons .submit-btn {
  flex: 1;
}

/* === NOTIFICATIONS === */
.notification-popup {
  position: fixed;
  top: 100px;
  right: 20px;
  z-index: 1001;
  min-width: 300px;
  max-width: 400px;
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  animation: slideInRight 0.5s ease-out;
  border-left: 5px solid;
}

.notification-popup.success {
  border-left-color: #10b981;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
}

.notification-popup .dashboard-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  font-weight: 600;
  color: #047857;
  text-decoration: none;
}

.notification-popup .dashboard-link:hover {
  text-decoration: underline;
}

.notification-popup.error {
  border-left-color: #ef4444;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
}

/* Image Preview Modal */
.preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1003;
  padding: 20px;
}

.preview-content {
  position: relative;
  max-width: 90%;
  max-height: 90%;
}

.preview-content img {
  max-width: 100%;
  max-height: 90vh;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.preview-close {
  position: absolute;
  top: -20px;
  right: -20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.preview-close:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: rotate(90deg);
}

/* === RESPONSIVE DESIGN === */
@media (max-width: 768px) {
  .upload-container {
    padding-top: 70px;
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }

  .upload-card {
    padding: 25px;
    border-radius: 20px;
    margin: 0 15px;
  }

  .page-title {
    font-size: 22px;
    margin: 0 15px 20px;
  }

  .step i {
    font-size: 18px;
  }

  .step span {
    font-size: 12px;
  }

  .title {
    font-size: 20px;
  }

  .nav-buttons {
    flex-direction: column;
  }

  .nav-buttons .back-btn,
  .nav-buttons .next-btn,
  .nav-buttons .submit-btn,
  .nav-buttons .nav-btn {
    width: 100%;
  }

  .modal-content.compact {
    max-width: 95%;
  }

  .notification-popup {
    top: 80px;
    right: 10px;
    left: 10px;
    min-width: auto;
    max-width: none;
  }

  .upload-area {
    padding: 40px 20px;
  }

  .upload-icon {
    font-size: 48px;
  }

  .upload-content h3 {
    font-size: 18px;
  }
}

@media (max-width: 480px) {
  .upload-card {
    padding: 20px;
    margin: 0 10px;
  }

  .page-title {
    font-size: 20px;
  }

  .steps .step-label {
    font-size: 11px;
  }

  .form-group input,
  .form-group textarea,
  .category-select select {
    padding: 12px 14px;
    font-size: 14px;
  }

  .next-btn,
  .back-btn,
  .submit-btn,
  .nav-btn {
    padding: 14px 20px;
    font-size: 14px;
    min-height: 44px;
  }

  .modal-header {
    padding: 20px 20px 12px;
  }

  .modal-body {
    padding: 12px 20px;
  }

  .modal-footer.compact {
    padding: 16px 20px;
  }

  .upload-actions {
    flex-direction: column;
    gap: 12px;
  }

  .gallery-stats {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

/* === ANIMATIONS === */
@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}

@keyframes slideUp {
  0% { opacity: 0; transform: translateY(30px); }
  100% { opacity: 1; transform: translateY(0); }
}

@keyframes slideInRight {
  0% { opacity: 0; transform: translateX(100%); }
  100% { opacity: 1; transform: translateX(0); }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.35s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(14px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}
</style>